// /src/pages/HomePage.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Terminal, Shield, Wrench, BookOpen } from 'lucide-react';
import { bodyText, ctaPrimary, ctaSecondary } from '../components/styles';
import { getPosts } from '../data/posts';

export default function HomePage() {
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    getPosts().then((posts) => setLatestPosts(posts.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="-mt-12 -mx-4">
      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden scanline-overlay">
        {/* Dot grid texture */}
        <div className="absolute inset-0 bg-dot-grid opacity-30" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-12 md:gap-16 items-start">
            {/* Left: Copy */}
            <div className="animate-stagger">
              <p className="font-mono text-sm text-[var(--accent)] mb-4 tracking-wide">
                <span className="opacity-50">$</span> whoami
              </p>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[var(--text)] leading-[1.08] tracking-tight mb-6">
                153k lines.<br />
                Zero compromises.
              </h1>

              <p className="text-lg text-[var(--muted)] max-w-xl leading-relaxed mb-8">
                Former IRS network admin turned cybersecurity founder. I build production-grade
                security platforms, not slide decks. HADES deploys on Kubernetes. VitalChain runs on AWS.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className={ctaPrimary}>
                  Book a 30-min threat review <ArrowRight size={18} />
                </Link>
                <Link to="/tools/hades" className={ctaSecondary}>
                  Explore HADES <ExternalLink size={18} />
                </Link>
              </div>
            </div>

            {/* Right: Terminal mockup */}
            <div className="terminal-window shadow-2xl">
              <div className="terminal-titlebar">
                <div className="terminal-dot" style={{ background: '#f85149' }} />
                <div className="terminal-dot" style={{ background: '#f0883e' }} />
                <div className="terminal-dot" style={{ background: '#3fb950' }} />
                <span className="text-xs text-[#6e7681] ml-2 font-mono">hades scan --deep</span>
              </div>
              <div className="terminal-body text-xs sm:text-sm">
                <div><span className="prompt">$</span> hades scan <span className="flag">--deep</span> invoice_q4.pdf</div>
                <div className="mt-2 comment"># Metadata extraction via ExifTool pool</div>
                <div className="mt-1">[<span className="flag">EXTRACT</span>] 47 metadata fields parsed</div>
                <div>[<span className="flag">YARA</span>] 3/8 rule sets triggered</div>
                <div>[<span className="flag">ML</span>] Ensemble score: <span className="result">0.91</span> (IsoForest + RF + XGB)</div>
                <div>[<span className="flag">FORMAT</span>] PDF: /OpenAction + /JavaScript detected</div>
                <div className="mt-2">[<span className="critical">THREAT</span>] Score: <span className="critical">87/100</span> | Severity: <span className="critical">CRITICAL</span></div>
                <div>[<span className="result">ATT&CK</span>] T1204.002 (User Execution: Malicious File)</div>
                <div>[<span className="result">ATT&CK</span>] T1059.007 (JavaScript)</div>
                <div className="mt-2">[<span className="flag">EVIDENCE</span>] Chain: SHA-256 linked | Case #2026-0315</div>
                <div className="mt-2 prompt">$ <span className="cursor-blink">_</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: 'linear-gradient(transparent, var(--bg))' }} />
      </section>

      {/* ═══ EVIDENCE STRIP ═══ */}
      <section className="relative" style={{ background: 'var(--bg2)' }}>
        <div className="max-w-7xl mx-auto px-4 py-14">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-4">
            {[
              { value: '153k+', label: 'Lines of Python', sub: 'HADES core' },
              { value: '269', label: 'Chaincode tests', sub: 'VitalChain' },
              { value: '51+', label: 'ATT&CK techniques', sub: 'Mapped' },
              { value: '297', label: 'Integration tests', sub: 'Passing' },
              { value: '83', label: 'Blog posts', sub: 'Published' },
            ].map((m, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="metric-value">{m.value}</div>
                <div className="text-sm font-semibold text-[var(--text)] mt-1">{m.label}</div>
                <div className="text-xs text-[var(--muted)]">{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
        <hr className="section-rule" />
      </section>

      {/* ═══ NARRATIVE ═══ */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="hidden md:block w-px self-stretch" style={{ background: 'linear-gradient(var(--accent), transparent)' }} />
          <div className="max-w-2xl space-y-5">
            <h2 className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase">
              {'// origin story'}
            </h2>
            <p className="text-lg text-[var(--text)] leading-relaxed" style={{ opacity: 0.9 }}>
              I went from supervising training in corrections to administering IRS networks to founding a
              cybersecurity firm. Each step taught me something the last one couldn't: corrections taught me
              how people break rules, the IRS taught me how infrastructure breaks down, and building DarkHorse
              taught me how to fix both at scale.
            </p>
            <p className="text-lg text-[var(--text)] leading-relaxed" style={{ opacity: 0.9 }}>
              Now I build the tools I wish existed when I was on the other side of the desk. HADES came from
              needing metadata forensics that didn't require executing suspect files. VitalChain came from
              watching paramedics arrive on scene with zero patient context.
            </p>
            <Link to="/about" className="text-[var(--accent)] font-semibold inline-flex items-center gap-1.5 hover:gap-3 transition-all text-sm">
              Read the full story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ WHAT I DO ═══ */}
      <section className="relative" style={{ background: 'var(--bg2)' }}>
        <hr className="section-rule" />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <h2 className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase mb-8">
            {'// active projects'}
          </h2>

          {/* Asymmetric staggered grid */}
          <div className="grid md:grid-cols-12 gap-6">
            {/* HADES: large card spanning 7 cols */}
            <div className="md:col-span-7 glow-card rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-8 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                <Shield size={128} />
              </div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <Terminal className="text-[var(--accent)]" size={20} />
                  <span className="font-mono text-xs text-[var(--accent)]">v1.0.0 PRODUCTION</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[var(--text)] mb-4">HADES Platform</h3>
                <p className={`${bodyText} leading-relaxed text-base mb-6`}>
                  Metadata forensics engine that catches what AV misses. YARA rules, ML ensemble scoring, and MITRE ATT&CK
                  mapping across images, documents, firmware, and archives, without executing a single file.
                </p>
                <Link to="/tools/hades" className="text-[var(--accent)] font-semibold inline-flex items-center gap-1.5 hover:gap-3 transition-all text-sm">
                  Deep-dive <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Right stack: 2 smaller cards */}
            <div className="md:col-span-5 flex flex-col gap-6">
              <div className="rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-7 flex-1 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 opacity-5">
                  <Wrench size={96} />
                </div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-2 h-2 rounded-full bg-[var(--accent)]" />
                    <span className="font-mono text-xs text-[var(--muted)]">PILOT</span>
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text)] mb-3">VitalChain Health</h3>
                  <p className="text-sm text-[var(--muted)] leading-relaxed">
                    Hyperledger Fabric blockchain giving paramedics instant access to patient records, even offline.
                    Merged with MedicaidChain for AI-powered fraud detection. Piloting with Vermont EMS.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-7 flex-1 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 opacity-5">
                  <BookOpen size={96} />
                </div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-2 h-2 rounded-full bg-[var(--accent-2)]" />
                    <span className="font-mono text-xs text-[var(--muted)]">ACTIVE</span>
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text)] mb-3">DarkHorse Consulting</h3>
                  <p className="text-sm text-[var(--muted)] leading-relaxed mb-4">
                    Pen testing, HIPAA audits, and incident response for healthcare, education, and government.
                    Not PowerPoint security; hands-on assessments from someone who builds the tools.
                  </p>
                  <Link to="/playbooks" className="text-[var(--accent)] font-semibold inline-flex items-center gap-1.5 hover:gap-3 transition-all text-sm">
                    Free security playbooks <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURED: HADES ═══ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="grid md:grid-cols-[1fr_auto] gap-12 items-start">
            <div>
              <h2 className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase mb-3">
                {'// featured platform'}
              </h2>
              <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--text)] leading-[1.1] mb-6">
                HADES
              </p>
              <p className="text-lg text-[var(--muted)] max-w-2xl leading-relaxed mb-8">
                153k lines of Python. 8 YARA rule sets. ML ensemble detection. 51+ MITRE ATT&CK technique mappings.
                Court-ready evidence chains. 10 automated response playbooks. Deployed via Kubernetes.
                This is what happens when a security consultant builds his own tools.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/tools/hades" className={ctaPrimary}>
                  Full Deep-Dive <ArrowRight size={18} />
                </Link>
                <a href="https://hades.darkhorseinfosec.com" target="_blank" rel="noopener noreferrer" className={ctaSecondary}>
                  Try Demo <ExternalLink size={18} />
                </a>
              </div>
            </div>

            {/* Vertical metrics */}
            <div className="hidden md:flex flex-col gap-6 border-l border-[color:var(--border)] pl-8">
              {[
                { val: '153k', label: 'Lines' },
                { val: '87', label: 'Test suites' },
                { val: '51+', label: 'ATT&CK' },
                { val: '10', label: 'Playbooks' },
              ].map((m, i) => (
                <div key={i}>
                  <div className="font-mono text-2xl font-bold text-[var(--accent)]">{m.val}</div>
                  <div className="text-xs text-[var(--muted)] uppercase tracking-wider">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <hr className="section-rule" />
      </section>

      {/* ═══ LATEST WRITING ═══ */}
      {latestPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase">
                {'// latest'}
              </h2>
            </div>
            <Link to="/blog" className="text-[var(--accent)] font-semibold inline-flex items-center gap-1.5 hover:gap-3 transition-all text-sm">
              All posts <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {latestPosts.map((post, i) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className={`blog-card block rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-6 ${i === 0 ? 'md:col-span-1' : ''}`}
              >
                <p className="font-mono text-xs text-[var(--muted)] mb-3">{post.date}</p>
                <h3 className="text-lg font-bold text-[var(--text)] mb-3 leading-snug line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-sm text-[var(--muted)] line-clamp-3 leading-relaxed">
                  {post.description || 'Read more...'}
                </p>
                <span className="inline-flex items-center gap-1 text-[var(--accent)] text-sm font-semibold mt-4">
                  Read <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
